import { useQueryClient } from "@tanstack/react-query";
import { FC, Fragment } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import Button from "@/components/Button";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
} from "@/components/SidePanel";
import Text from "@/components/Text";
import useAlerts from "@/hooks/useAlerts";
import { ProductionSceneCommentResource } from "@/types/resources";
import { api } from "@/utils/api";
import { RootState } from "@/utils/store";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";

const StyledSidePanelContent = styled(SidePanelContent)`
  padding: 24px 16px;
`;

const StyledSidePanel = styled(SidePanel)`
  & {
    width: 344px;
  }
`;

const Phrase = styled(Text)`
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CommentBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 8px;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const AuthorAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: red;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const Actions = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const CommentContent = styled(Text)`
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
  height: auto;
  width: 100%;
  &:focus {
    outline: none;
  }
`;

const ActionsWrap = styled.div`
  margin-left: auto;
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  z-index: 11;
`;

interface FormDataT {
  content: string;
}

interface Props {
  onClose: () => void;
  sceneId: number;
  discusion?: ProductionSceneCommentResource;
  element?: HTMLSpanElement;
  text?: string;
  onDiscusionCreated: (id: number) => void;
  saveContent: () => void;
}

const CommentsModal: FC<Props> = ({
  onClose,
  text,
  element,
  discusion,
  sceneId,
  onDiscusionCreated,
  saveContent,
}) => {
  const alerts = useAlerts();

  const { projectId } = useParams();

  const queryClient = useQueryClient();

  const author = useSelector((state: RootState) => ({
    id: state.auth.user!.id,
    name: state.auth.user!.name,
    avatar: state.auth.user!.avatar,
  }));

  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      content: "",
    },
  });

  const phrase = text ?? discusion?.phrase;

  const onDelete = async (commentId: number, isParent = false) => {
    try {
      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete this comment?"
      );

      if (!confirmed) return;

      await api.delete(`/v1/production/scenes/comments/${commentId}`);

      if (isParent) {
        saveContent();
        onClose();
      } else {
        await queryClient.invalidateQueries([
          "production_projects",
          { projectId },
        ]);
      }

      alerts.close();
    } catch (_) {
      alerts.error("Failed to delete comment!");
    }
  };

  const onSubmit = handleSubmit(async ({ content }) => {
    try {
      const { id } = await api.post<object, { id: number }>(
        `/v1/production/scenes/comments`,
        {
          content,
          parent_id: discusion?.id,
          production_scene_id: sceneId,
          phrase,
        }
      );

      element?.setAttribute("data-comment", id.toString());

      if (!discusion) {
        saveContent();
        onDiscusionCreated(id);
      } else {
        await queryClient.invalidateQueries([
          "production_projects",
          { projectId },
        ]);
      }

      reset();
    } catch (_) {
      alerts.error();
    }
  });

  const onCancel = () => {
    element?.remove();
    onClose();
  };

  return (
    <Modal>
      <StyledSidePanel heading="Comments" onClose={onCancel}>
        <SidePanelBody as="div">
          <StyledSidePanelContent>
            <Inner>
              <Phrase $type="smallBold">{phrase}</Phrase>
              <CommentBox>
                {!!discusion && (
                  <>
                    <CommentHeader>
                      {!!discusion.author.avatar && (
                        <AuthorAvatar
                          src={discusion.author.avatar}
                          alt={discusion.author.name}
                        />
                      )}
                      <Text $type="smallHeadline">
                        {discusion.author.name}
                        <Text as="span" $type="smallHeadline" $color="gray">
                          {" "}
                          · {discusion.postedAt}
                        </Text>
                      </Text>
                      {author.id === discusion.author.id && (
                        <ActionsWrap>
                          <StyledActionsDropdown>
                            <ActionsDropdownButton
                              onClick={() => {
                                onDelete(discusion.id, true);
                              }}
                              as="button"
                            >
                              <span>
                                <TrashIcon />
                              </span>
                              Delete
                            </ActionsDropdownButton>
                          </StyledActionsDropdown>
                        </ActionsWrap>
                      )}
                    </CommentHeader>
                    <CommentContent $type="caption">
                      {discusion.content}
                    </CommentContent>
                  </>
                )}
                {discusion?.comments?.map((comment) => (
                  <Fragment key={comment.id}>
                    <CommentHeader>
                      {!!comment.author.avatar && (
                        <AuthorAvatar
                          src={comment.author.avatar}
                          alt={comment.author.name}
                        />
                      )}
                      <Text $type="smallHeadline">
                        {comment.author.name}
                        <Text as="span" $type="smallHeadline" $color="gray">
                          {" "}
                          · {comment.postedAt}
                        </Text>
                      </Text>
                      {author.id === comment.author.id && (
                        <ActionsWrap>
                          <StyledActionsDropdown>
                            <ActionsDropdownButton
                              onClick={() => {
                                onDelete(comment.id);
                              }}
                              as="button"
                            >
                              <span>
                                <TrashIcon />
                              </span>
                              Delete
                            </ActionsDropdownButton>
                          </StyledActionsDropdown>
                        </ActionsWrap>
                      )}
                    </CommentHeader>
                    <CommentContent $type="caption">
                      {comment.content}
                    </CommentContent>
                  </Fragment>
                ))}
                <form onSubmit={onSubmit}>
                  <CommentHeader>
                    {!!author.avatar && (
                      <AuthorAvatar src={author.avatar} alt={author.name} />
                    )}
                    <Text $type="smallHeadline">{author.name}</Text>
                  </CommentHeader>
                  <CommentContent
                    $type="caption"
                    as="textarea"
                    rows={3}
                    placeholder="Add comment"
                    {...register("content")}
                  />
                  <Actions>
                    {discusion ? (
                      <Button
                        small
                        type="submit"
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                      >
                        Reply
                      </Button>
                    ) : (
                      <>
                        <Button
                          small
                          color="secondary"
                          type="button"
                          onClick={onCancel}
                        >
                          Cancel
                        </Button>
                        <Button
                          small
                          type="submit"
                          disabled={isSubmitting || !isDirty}
                          loading={isSubmitting}
                        >
                          Post
                        </Button>
                      </>
                    )}
                  </Actions>
                </form>
              </CommentBox>
            </Inner>
          </StyledSidePanelContent>
        </SidePanelBody>
      </StyledSidePanel>
    </Modal>
  );
};

export default CommentsModal;
